import { ProjectGrantsGate } from '@/components/features/projects/ProjectGrantsGate'
import { ProjectTabBar } from '@/components/features/projects/ProjectTabBar'
import NotFound from '@/components/utils/NotFound'
import RouteLoadFailed from '@/components/utils/RouteLoadFailed'
import { useClient } from '@/context/ClientContext'
import { useOrganisationUsersWithUserMap } from '@/hooks/useOrganisationUsersWithUserMap'
import { usePermissionCheck } from '@/hooks/usePermissionCheck'
import { useProjectSummary } from '@/hooks/useProjectSummary'
import { throwLoaderError } from '@/lib/loader-errors'
import { loadProjectHeroImage } from '@/lib/projectHero'
import { userDisplayName, userFor } from '@/lib/userDisplay'
import { cn, formatBytes, formatDate } from '@/lib/utils'
import {
  CheckIcon,
  ClipboardDocumentIcon,
  MapIcon,
} from '@heroicons/react/24/outline'
import { useQuery } from '@tanstack/react-query'
import {
  Link,
  Outlet,
  createFileRoute,
  useLocation,
} from '@tanstack/react-router'
import { useState } from 'react'
import { z } from 'zod'

import { HeroPanel } from '@droidmap/frontend-ui'
import {
  DroidMapMetadata,
  StorageSummary,
} from '@droidmap/shared-service-contract'

export const Route = createFileRoute(
  '/_authenticated/orgs/$orgId_/projects/$projectId',
)({
  params: {
    parse: (params) => ({
      projectId: z.string().parse(params.projectId),
    }),
  },
  beforeLoad: async ({ params, context }) => {
    const { orgId, projectId } = params
    const metadata: DroidMapMetadata = {
      ...context.metadata,
      orgId,
      projectId,
    }
    return { metadata }
  },
  loader: async ({ params, context }) => {
    const { orgId, projectId } = params
    const { projectClient } = context.client
    try {
      const project = await projectClient.getProject(
        { projectId },
        context.metadata,
      )
      return { crumb: project.name, orgId, projectId, project }
    } catch (error) {
      // A 5xx or a network blip is not a missing project (#2658) — only a
      // 404/403 reaches `notFoundComponent`; everything else gets a retry.
      throwLoaderError(error, 'project')
    }
  },
  component: RouteComponent,
  notFoundComponent: () => <NotFound resource="Project" />,
  errorComponent: () => <RouteLoadFailed resource="project" />,
})

function IdCopy({ id }: { id: string }) {
  const [copied, setCopied] = useState(false)
  const onClick = async (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    try {
      await navigator.clipboard.writeText(id)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      // Clipboard access can fail in non-secure contexts.
    }
  }
  return (
    <button
      type="button"
      onClick={onClick}
      title="Copy ID"
      className={cn(
        'bg-bg-2 border-border text-text-dim inline-flex items-center gap-2 rounded-xs border px-2.5 py-1 font-mono text-[11px] transition-colors duration-150',
        'hover:text-accent hover:border-accent-tint',
      )}
    >
      {copied ? (
        <CheckIcon className="h-3 w-3" />
      ) : (
        <ClipboardDocumentIcon className="h-3 w-3" />
      )}
      <span className="max-w-[18rem] truncate">{id}</span>
    </button>
  )
}

// Sum of the per-tier numeric values on a StorageSummary. The contract
// holds tiered byte counts (standard, IA, glacier, …); we want the total.
const totalStorageBytes = (s: StorageSummary | undefined): number => {
  if (!s) return 0
  return (Object.values(s) as unknown[]).reduce<number>(
    (sum, v) => sum + (typeof v === 'number' ? v : 0),
    0,
  )
}

function RouteComponent() {
  const { orgId, projectId, project } = Route.useLoaderData()
  const { metadata } = Route.useRouteContext()
  const location = useLocation()
  const isPermissionsTab = /\/permissions(?:\/|$)/.test(location.pathname)
  const { assetClient, organisationClient, userClient } = useClient()

  // Permission context (orgId + projectId).
  usePermissionCheck()

  const { data: heroImageUrl } = useQuery({
    queryKey: ['projectHero', projectId],
    queryFn: () => loadProjectHeroImage(assetClient, projectId, metadata),
    staleTime: 5 * 60 * 1000,
  })

  // Parent org — for the "in <org>" context line that anchors the project
  // within its organisation (the project route breaks out of the $orgId
  // layout, so the org isn't available as parent loader data here).
  const { data: parentOrg } = useQuery({
    queryKey: ['organisation', orgId],
    queryFn: () => organisationClient.getOrganisation({ orgId }, metadata),
    staleTime: 5 * 60 * 1000,
  })

  const { userMap } = useOrganisationUsersWithUserMap({
    orgId,
    organisationClient,
    userClient,
    metadata,
  })

  const summary = useProjectSummary(projectId, metadata)
  const { counts } = summary

  const owner = userFor(userMap, project?.createdBy)
  const ownerName = owner ? userDisplayName(owner) : undefined

  const created = project?.created
    ? formatDate(project.created, { format: 'dd MMM yyyy' })
    : null

  // Split the formatted bytes string ("557.1 MiB") so the unit can be
  // styled separately from the number — the value uses the project's mono
  // hero typography while the unit is rendered smaller and accent-coloured.
  const sizeBytes = totalStorageBytes(project?.storageSummary)
  const sizeFormatted = sizeBytes > 0 ? formatBytes(sizeBytes, 1) : null
  const sizeNum = sizeFormatted?.split(' ')[0] ?? '0'
  const sizeUnit = sizeFormatted?.split(' ')[1] ?? 'B'

  return (
    <div>
      <HeroPanel
        className="mb-6"
        edgeClassName="bg-accent"
        backgroundImageUrl={heroImageUrl ?? undefined}
        media={
          <div className="border-border bg-bg-2 grid h-[60px] w-[60px] place-items-center overflow-hidden rounded-md border shadow-elevated">
            {heroImageUrl ? (
              <img
                src={heroImageUrl}
                alt=""
                className="h-full w-full object-cover"
              />
            ) : (
              <MapIcon className="text-accent h-7 w-7 opacity-80" />
            )}
          </div>
        }
        eyebrow={
          <span className="inline-flex items-center gap-2.5">
            <span className="inline-flex items-center gap-2 rounded-full border border-accent/25 bg-accent-soft px-2.5 py-1">
              <span
                aria-hidden
                className="bg-accent inline-block h-1.5 w-1.5 rounded-full shadow-[0_0_10px_currentColor] animate-pulse"
              />
              Project
            </span>
            <span className="text-text-mute normal-case">in</span>
            <Link
              to="/orgs/$orgId"
              params={{ orgId }}
              className="text-text-dim hover:text-accent normal-case transition-colors"
            >
              {parentOrg?.name ?? 'organisation'}
            </Link>
          </span>
        }
        title={project?.name || 'Project'}
        actions={
          <div className="flex flex-wrap items-end gap-8 pr-1">
            <div className="text-right">
              <div className="text-text font-mono text-[26px] font-medium leading-none tracking-tight">
                {summary.assetSets.isLoading ? (
                  <span className="text-text-mute">··</span>
                ) : (
                  counts.assetSets.toLocaleString()
                )}
              </div>
              <div className="text-micro text-text-mute mt-2">Asset Sets</div>
            </div>
            <div className="text-right">
              <div className="text-text font-mono text-[26px] font-medium leading-none tracking-tight">
                {(project?.assetCount ?? 0).toLocaleString()}
              </div>
              <div className="text-micro text-text-mute mt-2">Assets</div>
            </div>
            <div className="text-right">
              <div className="text-text font-mono text-[26px] font-medium leading-none tracking-tight">
                {sizeNum}{' '}
                <em className="text-accent text-[17px] not-italic">
                  {sizeUnit}
                </em>
              </div>
              <div className="text-micro text-text-mute mt-2">Size</div>
            </div>
          </div>
        }
        meta={
          <>
            <IdCopy id={projectId} />
            {created && (
              <>
                <span className="text-text-mute">·</span>
                <span>
                  Created{' '}
                  <strong className="text-text font-medium">{created}</strong>
                </span>
              </>
            )}
            {ownerName && (
              <>
                <span className="text-text-mute">·</span>
                <span>
                  Owner{' '}
                  <strong className="text-text font-medium">{ownerName}</strong>
                </span>
              </>
            )}
          </>
        }
      />

      <ProjectTabBar orgId={orgId} projectId={projectId} />

      {isPermissionsTab ? (
        <ProjectGrantsGate projectId={projectId}>
          <Outlet />
        </ProjectGrantsGate>
      ) : (
        <Outlet />
      )}
    </div>
  )
}
